import React, { useState, useEffect, useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import PublicSEO from '../../components/public/PublicSEO';
import { stripHtml } from '../../utils/html';
import { getSnapApiData } from '../../utils/snapStore';
import { buildBlog, buildBreadcrumb, buildBlogItemList } from '../../utils/jsonLd';
import {
  BLOG_PER_PAGE,
  blogListingCanonicalPath,
  blogListingPath,
} from '../../config/blogPagination';
import { publicPath } from '../../utils/canonicalUrl';
import './BlogPage.css';
import './BlogPublicScope.css';

const BlogPage = () => {
  const { pageNum } = useParams();
  const navigate = useNavigate();
  const snapArticoli = getSnapApiData('/public/articoli');
  const snapCategorie = getSnapApiData('/public/categorie');
  const initialArticoli = Array.isArray(snapArticoli) ? snapArticoli : [];
  const initialCategorie = Array.isArray(snapCategorie) ? snapCategorie : [];
  const [articoli, setArticoli] = useState(initialArticoli);
  const [categorie, setCategorie] = useState(initialCategorie);
  const [loading, setLoading] = useState(initialArticoli.length === 0);
  const [error, setError] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  const pageFromUrl = useMemo(() => {
    if (!pageNum) return 1;
    const parsed = parseInt(pageNum, 10);
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
  }, [pageNum]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const fetchAll = () => Promise.all([
        api.get('/public/articoli'),
        api.get('/public/categorie'),
      ]);
      try {
        let artRes;
        let catRes;
        try {
          [artRes, catRes] = await fetchAll();
        } catch {
          await new Promise((r) => setTimeout(r, 400));
          if (cancelled) return;
          [artRes, catRes] = await fetchAll();
        }
        if (cancelled) return;
        setArticoli(Array.isArray(artRes.data) ? artRes.data : []);
        setCategorie(Array.isArray(catRes.data) ? catRes.data : []);
        setError(null);
      } catch (err) {
        console.error('Errore caricamento articoli/categorie:', err);
        if (!cancelled) {
          setArticoli((prev) => {
            if (prev.length === 0) {
              setError(err.response?.data?.message || 'Impossibile caricare gli articoli.');
            }
            return prev;
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const filtered = selectedCategoryId == null
    ? articoli
    : articoli.filter((a) => a.idCategoria === selectedCategoryId);
  const totalPages = Math.max(1, Math.ceil(filtered.length / BLOG_PER_PAGE));
  const page = Math.min(pageFromUrl, totalPages);
  const start = (page - 1) * BLOG_PER_PAGE;
  const paginated = filtered.slice(start, start + BLOG_PER_PAGE);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategoryId(categoryId);
    if (pageFromUrl !== 1) {
      navigate(blogListingPath(1), { replace: true });
    }
  };

  if (pageNum && pageFromUrl < 2) {
    return <Navigate to={blogListingPath(1)} replace />;
  }

  const canonicalPath = blogListingCanonicalPath(pageFromUrl > 1 ? pageFromUrl : 1);
  const pageTitle = pageFromUrl > 1 ? `Argomenti - Pagina ${pageFromUrl}` : 'Argomenti - Salute e telemedicina';

  if (loading && articoli.length === 0 && !error) {
    return (
      <div className="blog-page vc-blog-surface">
        <PublicSEO
          title={pageTitle}
          description="Articoli su salute, telemedicina, visite mediche online e consigli per prenderti cura di te. Gli argomenti di Virtual Clinic, scritti da medici e specialisti."
          canonicalPath={canonicalPath}
          keywords="blog salute, articoli telemedicina, visite mediche online, consigli salute, salute online"
          ogImageAlt="Argomenti - Blog di Virtual Clinic sulla salute"
        />
        <div className="blog-page-container">
          <header className="blog-page-header">
            <h1 className="blog-page-title">Argomenti</h1>
            <p className="blog-page-intro">
              Articoli su salute, telemedicina e visite mediche online. Consigli e approfondimenti dai nostri specialisti.
            </p>
            <div className="blog-category-filters is-empty" aria-hidden="true" />
          </header>
          <div className="blog-page-loading-shell" aria-busy="true" aria-label="Caricamento articoli" />
        </div>
      </div>
    );
  }

  if (!loading && !error && pageNum && pageFromUrl > totalPages) {
    return <Navigate to={blogListingPath(totalPages)} replace />;
  }

  const prevPath = page > 1 ? blogListingCanonicalPath(page - 1) : null;
  const nextPath = page < totalPages ? blogListingCanonicalPath(page + 1) : null;
  const listingCanonicalPath = blogListingCanonicalPath(page);
  const listingPageTitle = page > 1 ? `Argomenti - Pagina ${page}` : 'Argomenti - Salute e telemedicina';

  const jsonLd = [
    buildBlog({
      path: listingCanonicalPath,
      name: listingPageTitle,
      description: 'Articoli e approfondimenti su salute, telemedicina e visite mediche online a cura di Virtual Clinic.',
    }),
    buildBreadcrumb([
      { name: 'Home', path: '/' },
      { name: 'Argomenti', path: '/blog' },
      ...(page > 1 ? [{ name: `Pagina ${page}` }] : []),
    ]),
    ...(articoli.length > 0 ? [buildBlogItemList(articoli)] : []),
  ];

  return (
    <div className="blog-page vc-blog-surface">
      <PublicSEO
        title={listingPageTitle}
        description="Articoli su salute, telemedicina, visite mediche online e consigli per prenderti cura di te. Gli argomenti di Virtual Clinic, scritti da medici e specialisti."
        canonicalPath={listingCanonicalPath}
        linkPrev={prevPath}
        linkNext={nextPath}
        keywords="blog salute, articoli telemedicina, visite mediche online, consigli salute, salute online"
        ogImageAlt="Argomenti - Blog di Virtual Clinic sulla salute"
        jsonLd={jsonLd}
      />
      <div className="blog-page-container">
        <header className="blog-page-header">
          <h1 className="blog-page-title">Argomenti</h1>
          <p className="blog-page-intro">
            Articoli su salute, telemedicina e visite mediche online. Consigli e approfondimenti dai nostri specialisti.
            {articoli.length === 0 && !loading && !error && (
              <> Contenuti in preparazione: torna a breve.</>
            )}
          </p>

          <div
            className={`blog-category-filters${categorie.length === 0 ? ' is-empty' : ''}`}
            role="group"
            aria-label="Filtra per categoria"
            aria-hidden={categorie.length === 0}
          >
            {categorie.length > 0 && (
              <>
              <button
                type="button"
                className={`blog-category-btn ${selectedCategoryId == null ? 'active' : ''}`}
                onClick={() => handleCategoryChange(null)}
              >
                Tutti
              </button>
              {categorie.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`blog-category-btn ${selectedCategoryId === cat.id ? 'active' : ''}`}
                  onClick={() => handleCategoryChange(cat.id)}
                >
                  {cat.categoria || `Categoria ${cat.id}`}
                </button>
              ))}
              </>
            )}
          </div>
        </header>

        {error && <p className="blog-page-error">{error}</p>}

        {!error && articoli.length === 0 && !loading && (
          <p className="blog-page-empty">Nessun articolo pubblicato al momento. Torna a breve!</p>
        )}

        {!error && filtered.length === 0 && articoli.length > 0 && (
          <p className="blog-page-empty">Nessun articolo in questa categoria.</p>
        )}

        {!error && paginated.length > 0 && (
          <>
            <div className="blog-grid" role="list">
              {paginated.map((art) => (
                <article key={art.id} className="blog-card" role="listitem">
                  <Link to={publicPath(`/blog/${art.slug || art.id}`)} className="blog-card-link">
                    {art.urlImg && (
                      <div className="blog-card-img-wrap">
                        <img src={art.urlImg} alt="" loading="lazy" />
                      </div>
                    )}
                    <div className="blog-card-body">
                      <time className="blog-card-date" dateTime={art.dataCreazione}>
                        {formatDate(art.dataCreazione)}
                      </time>
                      <h2 className="blog-card-title">{art.titolo}</h2>
                      {art.intro && <p className="blog-card-intro">{stripHtml(art.intro)}</p>}
                    </div>
                  </Link>
                </article>
              ))}
            </div>

            {totalPages > 1 && (
              <nav className="blog-pagination" aria-label="Paginazione articoli">
                {page > 1 ? (
                  <Link
                    to={blogListingPath(page - 1)}
                    className="blog-pagination-link"
                    rel="prev"
                    aria-label="Pagina precedente"
                  >
                    Precedente
                  </Link>
                ) : (
                  <span className="blog-pagination-link is-disabled" aria-hidden="true">Precedente</span>
                )}

                <div className="blog-pagination-pages" role="list">
                  {Array.from({ length: totalPages }, (_, index) => {
                    const pageNumber = index + 1;
                    const isCurrent = pageNumber === page;
                    return (
                      <Link
                        key={pageNumber}
                        to={blogListingPath(pageNumber)}
                        className={`blog-pagination-page${isCurrent ? ' is-current' : ''}`}
                        aria-label={`Pagina ${pageNumber}`}
                        aria-current={isCurrent ? 'page' : undefined}
                      >
                        {pageNumber}
                      </Link>
                    );
                  })}
                </div>

                {page < totalPages ? (
                  <Link
                    to={blogListingPath(page + 1)}
                    className="blog-pagination-link"
                    rel="next"
                    aria-label="Pagina successiva"
                  >
                    Successiva
                  </Link>
                ) : (
                  <span className="blog-pagination-link is-disabled" aria-hidden="true">Successiva</span>
                )}
              </nav>
            )}
          </>
        )}

        {!error && articoli.length > 0 && (
          <nav className="blog-archive-links" aria-label="Archivio completo articoli">
            <h2 className="blog-archive-title">Tutti gli articoli</h2>
            <ul className="blog-archive-list">
              {articoli.map((art) => (
                <li key={art.id}>
                  <Link to={publicPath(`/blog/${art.slug || art.id}`)}>
                    {art.titolo}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
